import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
  useDisclosure,
  useToast,
  useToken,
} from "@chakra-ui/react";
import { stringMembers } from "../../../strings";
import ShowDeletedToggle from "../../../components/show-deleted-toggle";
import { useContextTypeSettingsOrganization } from "../dashboard-layout";
import {
  AccountingSystemBill,
  AccountingSystemConfig,
  CreateAccountingSystemBillInput,
  CreateUserBookkeepingInput,
  CreateUserInput,
  CreateUserOrganizationInput,
  CurrencyCode,
  ListAccountingSystemBillsQuery,
  ListAccountingSystemBillsQueryVariables,
  ListAccountingSystemConfigsQuery,
  ListAccountingSystemConfigsQueryVariables,
  ListOrganizationsQuery,
  ListOrganizationsQueryVariables,
  OrganizationType,
  UpdateAccountingSystemBillInput,
  UpdateUserBookkeepingInput,
  UpdateUserInput,
  UpdateUserOrganizationInput,
  UserAppRole,
  UserOrganization,
  UserOrganizationRole,
} from "../../../API";
import { FieldArray, Form, Formik } from "formik";
import {
  DeletedAt,
  Organization,
  OrganizationFull,
  User,
} from "../../../models";
import { useNavigate } from "react-router-dom";
import {
  createUser,
  createUserOrganization,
  createUserBookkeeping,
  updateUser,
  updateUserOrganization,
  updateUserBookkeeping,
  createAccountingSystemBill,
  updateAccountingSystemBill,
} from "../../../graphql/mutations";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import {
  aerzteTreuhandLogo,
  deleteItem,
  emailAlreadyExists,
  emailValidation,
  formatDate,
  queryList,
} from "../../../utils";
import { useUserOrganizations } from "../../../hooks/use-user-organizations";
import TeamTable from "./team-table";
import { useUsers } from "../../../hooks/use-users";
import { useUserBookkeepings } from "../../../hooks/use-user-bookkeepings";
import { useEffect, useRef, useState } from "react";
import {
  listAccountingSystemBills,
  listAccountingSystemConfigs,
  listOrganizations,
} from "../../../graphql/queries";
import * as Yup from "yup";
import { getUnixTime } from "date-fns";
import { ReactComponent as IconCancel } from "../../../assets/icons/xClose.svg";
import { v4 as guid } from "uuid";

export type TeamValue = Pick<User, "id" | "lastName" | "firstName" | "email"> &
  DeletedAt & {
    appRole: User["role"];
    organizationRole: UserOrganization["role"];
    approverUserId?: string | null;
    bookkeepings: {
      id: string;
      name: string;
    }[];
  };

type TeamValueFlat = Omit<TeamValue, "bookkeepings"> & {
  bookkeepingId?: string;
};

export type TeamValues = {
  members?: TeamValue[];
};

const typeNamePlural = stringMembers;

const Team = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [gray700] = useToken("colors", ["gray.700"]);
  const [showDeleted, setShowDeleted] = useState(false);

  const { user, selectedOrganization, bookkeepings } =
    useContextTypeSettingsOrganization();
  const { userOrganizations } = useUserOrganizations({
    selectedOrganization,
    withAccountants:
      user?.role === UserAppRole.accountant &&
      selectedOrganization?.type !== OrganizationType.accountant,
    includeDeleted: true,
  });
  const { users } = useUsers({
    userOrganizations,
    includeDeleted: true,
  });

  const hasDeletedItems =
    users?.some((user) => user.deletedAt) ||
    userOrganizations?.some((uo) => uo.deletedAt) ||
    false;
  const { userBookkeepings } = useUserBookkeepings({ bookkeepings });
  const [accountingSystemConfigs, setAccountingSystemConfigs] = useState<
    AccountingSystemConfig[] | undefined
  >();
  const [accountingSystemBills, setAccountingSystemBills] = useState<
    AccountingSystemBill[] | undefined
  >();

  // Fetch accountants if user is an accountant
  // and selectedOrganization is not an accountant organization
  const [accountantOrganizations, setAccountantOrganizations] =
    useState<Organization[]>();
  const {
    userOrganizations: accountantUserOrganizations,
    setUserOrganizations: setAccountantUserOrganizations,
  } = useUserOrganizations({
    selectedOrganization:
      accountantOrganizations && accountantOrganizations.length > 0
        ? accountantOrganizations[0]
        : undefined,
  });
  const { users: accountantUsers } = useUsers({
    userOrganizations: accountantUserOrganizations,
  });

  useEffect(() => {
    const bookkeepingsBexio = bookkeepings?.filter(
      (b) => b.accountingSystem === "bexio"
    );
    if (bookkeepingsBexio && bookkeepingsBexio.length > 0) {
      const fetchAccountingSystemConfigs = async () => {
        const accountingSystemConfigs = await queryList<
          AccountingSystemConfig,
          ListAccountingSystemConfigsQueryVariables,
          ListAccountingSystemConfigsQuery
        >(listAccountingSystemConfigs, {
          filter: {
            or: bookkeepingsBexio.map((bookkeeping) => ({
              bookkeepingId: {
                eq: bookkeeping.id,
              },
            })),
          },
        });
        setAccountingSystemConfigs(accountingSystemConfigs);
      };
      fetchAccountingSystemConfigs();
    }
  }, [bookkeepings]);

  useEffect(() => {
    const bookkeepingsBexio = bookkeepings?.filter(
      (b) => b.accountingSystem === "bexio"
    );
    if (userBookkeepings && bookkeepingsBexio && bookkeepingsBexio.length > 0) {
      const fetchAccountingSystemBills = async () => {
        const accountingSystemBills = await queryList<
          AccountingSystemBill,
          ListAccountingSystemBillsQueryVariables,
          ListAccountingSystemBillsQuery
        >(listAccountingSystemBills, {
          filter: {
            or: userBookkeepings.map((ub) => ({
              userBookkeepingId: {
                eq: ub.id,
              },
            })),
          },
        });
        setAccountingSystemBills(accountingSystemBills);
      };
      fetchAccountingSystemBills();
    }
  }, [bookkeepings, userBookkeepings]);

  useEffect(() => {
    if (user && user.role === UserAppRole.accountant) {
      if (selectedOrganization?.type !== OrganizationType.accountant) {
        const fetchAccountantOrganizations = async () => {
          const accountantOrganizations = await queryList<
            Organization,
            ListOrganizationsQueryVariables,
            ListOrganizationsQuery
          >(listOrganizations, {
            filter: {
              type: {
                eq: OrganizationType.accountant,
              },
            },
          });
          setAccountantOrganizations(accountantOrganizations);
        };
        fetchAccountantOrganizations();
      } else {
        setAccountantOrganizations(undefined);
        setAccountantUserOrganizations(undefined);
      }
    }
  }, [selectedOrganization, setAccountantUserOrganizations, user]);

  const initialValues: TeamValues = {
    members: [
      ...(userOrganizations ? [...userOrganizations] : []),
      ...(accountantUserOrganizations
        ? [
            ...accountantUserOrganizations.filter(
              (auo) =>
                !userOrganizations?.some((uo) => uo.userId === auo.userId)
            ),
          ]
        : []),
    ]
      ?.map((uo) => {
        const user =
          users?.find((u) => u.id === uo.userId) ??
          accountantUsers?.find((u) => u.id === uo.userId);
        return {
          id: user?.id,
          lastName: user?.lastName,
          firstName: user?.firstName,
          email: user?.email,
          appRole: user?.role,
          organizationRole:
            // Show Accountant role if user is an accountant
            accountantUserOrganizations?.find((auo) => auo.id === uo.id)
              ? UserOrganizationRole.accountant
              : uo.role,
          approverUserId: uo.approverUserId ?? null,
          bookkeepings:
            userBookkeepings
              ?.filter((ub) => ub.userId === user?.id)
              .map((ub) => ({
                id: ub.bookkeepingId,
                name:
                  bookkeepings?.find((b) => b.id === ub.bookkeepingId)?.name ??
                  "",
              })) ?? [],
          deletedAt: user?.deletedAt,
        } as TeamValue;
      })
      .filter((m) => m.id)
      .filter((m) => showDeleted || !m.deletedAt)
      // Deleted items first (most recently deleted at top), then Organization Admin, then Accountants last
      .toSorted((a, b) => {
        // Deleted items first, most recently deleted at top
        if (a.deletedAt && !b.deletedAt) return -1;
        if (!a.deletedAt && b.deletedAt) return 1;
        if (a.deletedAt && b.deletedAt) {
          return a.deletedAt > b.deletedAt ? -1 : 1;
        }
        // Then Organization Admin first
        if (a.appRole === UserAppRole.organizationAdmin) {
          return -1; // 'a' should come before 'b'
        } else if (b.appRole === UserAppRole.organizationAdmin) {
          return 1; // 'b' should come before 'a'
        } else if (a.appRole === UserAppRole.accountant) {
          return 1; // 'a' should come after 'b'
        } else if (b.appRole === UserAppRole.accountant) {
          return -1; // 'b' should come after 'a'
        }
        return 0; // 'a' and 'b' are considered equal
      }),
  };

  const TeamSchema = Yup.object().shape({
    members: Yup.array().of(
      Yup.object().shape({
        lastName: Yup.string().required("Nachname ist erforderlich"),
        firstName: Yup.string().required("Vorname ist erforderlich"),
        email: emailValidation(
          [
            ...(users ? [...users] : []),
            ...(accountantUsers ? [...accountantUsers] : []),
          ],
          selectedOrganization?.id
        ).test("unique-email", emailAlreadyExists, function (value) {
          const { options } = this;

          const allMembers = options.context?.members || [];
          const duplicateCount = allMembers.filter(
            (m: TeamValue) => m.email === value
          ).length;

          return duplicateCount <= 1; // false => test fails => validation error
        }),
      })
    ),
  });

  type UpcomingInvoice = {
    currency: CurrencyCode;
    lines: {
      data: {
        amount: number;
        currency: CurrencyCode;
        description: string;
        quantity: number;
      }[];
    };
    subscription_proration_date: number;
    subtotal: number;
    tax: number;
    total: number;
  };

  type UpcomingInvoiceModalProps = {
    upcomingInvoice?: UpcomingInvoice;
    userFirst?: User;
    membersFlat?: TeamValueFlat[];
    toAddUsersCognito?:
      | Pick<
          User,
          | "id"
          | "lastName"
          | "firstName"
          | "email"
          | "role"
          | "authGroupsRead"
          | "authGroupsUpdate"
        >[];
    toRemoveUsers?: UpdateUserInput[];
    // Target seat quantity (authoritative — passed straight to Stripe on
    // apply instead of re-deriving from invoice lines).
    targetQuantity?: number;
    // Seat reduction → defer credit to renewal (proration_behavior "none",
    // no proration_date). Mirrors the preview call so apply can't diverge.
    isReduction?: boolean;
    closed?: boolean;
    confirmed?: boolean;
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const buttonRef = useRef(null);
  const [upcomingInvoiceModalProps, setUpcomingInvoiceModalProps] =
    useState<UpcomingInvoiceModalProps>({});

  useEffect(() => {
    if (upcomingInvoiceModalProps.closed !== undefined) {
      if (upcomingInvoiceModalProps.closed) {
        onClose();
      } else {
        onOpen();
      }
    }
  }, [upcomingInvoiceModalProps.closed, onClose, onOpen]);

  const onModalClose = () => {
    setUpcomingInvoiceModalProps({});
    onClose();
  };

  // Execute changes if user confirms upcoming invoice
  useEffect(() => {
    if (
      selectedOrganization &&
      upcomingInvoiceModalProps.userFirst &&
      upcomingInvoiceModalProps.membersFlat &&
      upcomingInvoiceModalProps.toAddUsersCognito &&
      upcomingInvoiceModalProps.toRemoveUsers &&
      ((!upcomingInvoiceModalProps.closed &&
        upcomingInvoiceModalProps.confirmed) ||
        (upcomingInvoiceModalProps.closed &&
          !upcomingInvoiceModalProps.confirmed))
    ) {
      const updateTeam = async () => {
        const userOrganizationFirst = userOrganizations![0];

        // First create Cognito users so that we can get the user id
        const headers = {
          Authorization: `${(await Auth.currentSession())
            .getAccessToken()
            .getJwtToken()}`,
          "Content-Type": "application/json",
        };

        const createUsersCognitoPromises =
          upcomingInvoiceModalProps.toAddUsersCognito?.map(async (u) => {
            const response = await API.post("AdminQueries", "/createUser", {
              headers: headers,
              body: {
                email: u.email,
                lastName: u.lastName,
                firstName: u.firstName,
                organizationId: selectedOrganization?.id,
              },
            });
            const userId = response.User.Username;
            const member = upcomingInvoiceModalProps.membersFlat?.find(
              (m) => m.id === u.id
            );
            return { ...member, id: userId } as TeamValueFlat;
          });

        const updatedMembers = await Promise.all(createUsersCognitoPromises!);

        const membersUpdated = upcomingInvoiceModalProps.membersFlat?.map(
          (member) => {
            const updatedMember = updatedMembers.find(
              (u) => u.email === member.email
            );
            return updatedMember ?? member;
          }
        );

        // Create
        const toAddUsers: CreateUserInput[] | undefined = getToAddUsers(
          selectedOrganization,
          upcomingInvoiceModalProps.userFirst!,
          membersUpdated,
          users
        );

        const toAddUserOrganizations:
          | CreateUserOrganizationInput[]
          | undefined = membersUpdated
          ?.filter((m) => !userOrganizations?.find((uo) => uo.userId === m.id))
          .map((m) => {
            return {
              userId: m.id,
              organizationId: selectedOrganization.id,
              role: m.organizationRole,
              authGroupsRead: userOrganizationFirst.authGroupsRead,
              authGroupsUpdate: userOrganizationFirst.authGroupsUpdate,
            };
          });

        // Add a user bookkeeping for each bookkeeping in member
        const toAddUserBookkeepings: CreateUserBookkeepingInput[] | undefined =
          membersUpdated
            ?.filter(
              (m) =>
                !userBookkeepings?.find(
                  (ub) =>
                    ub.userId === m.id && ub.bookkeepingId === m.bookkeepingId
                ) && m.bookkeepingId
            )
            .flat()
            .map((m) => {
              const userBookkeepingFirst = userBookkeepings?.find(
                (ub) => ub.bookkeepingId === m.bookkeepingId
              );
              if (userBookkeepingFirst) {
                return {
                  id: guid(),
                  bookkeepingId: m.bookkeepingId!,
                  userId: m.id,
                  authGroupsRead: userBookkeepingFirst.authGroupsRead,
                  authGroupsUpdate: userBookkeepingFirst.authGroupsUpdate,
                };
              } else {
                return undefined;
              }
            })
            .filter(Boolean) as CreateUserBookkeepingInput[];

        const toAddAccountingSystemBills:
          | CreateAccountingSystemBillInput[]
          | undefined = toAddUserBookkeepings
          ?.map((aub) => {
            const userBookkeepingFirst = userBookkeepings?.find(
              (ub) => ub.bookkeepingId === aub.bookkeepingId
            );
            const accountingSystemConfig = accountingSystemConfigs?.find(
              (asc) => asc.bookkeepingId === aub.bookkeepingId
            );
            const accountingSystemBillFirst = accountingSystemBills?.find(
              (asb) => asb.userBookkeepingId === userBookkeepingFirst?.id
            );
            if (accountingSystemConfig && accountingSystemBillFirst) {
              return {
                authUserId: aub.userId,
                baseCurrency: accountingSystemConfig.baseCurrency,
                senderBankAccountId: accountingSystemConfig.senderBankAccountId,
                billingPeriod: accountingSystemConfig.billingPeriod,
                userBookkeepingId: aub.id!,
                contactPartnerId: accountingSystemBillFirst.contactPartnerId,
                authGroups: aub.authGroupsUpdate,
              };
            } else {
              return undefined;
            }
          })
          .filter(Boolean) as CreateAccountingSystemBillInput[];

        // Update
        const toUpdateUsers: UpdateUserInput[] | undefined = membersUpdated
          ?.filter((m) => users?.find((u) => u.id === m.id))
          ?.map((m) => {
            const user = users?.find((u) => u.id === m.id);
            return {
              id: m.id,
              ...(m.lastName !== user?.lastName
                ? { lastName: m.lastName }
                : {}),
              ...(m.firstName !== user?.firstName
                ? { firstName: m.firstName }
                : {}),
              ...(m.email !== user?.email ? { email: m.email } : {}),
            };
          })
          .filter((u) => Object.keys(u).length > 1);

        const toUpdateUserOrganizations:
          | UpdateUserOrganizationInput[]
          | undefined = membersUpdated
          ?.map((m) => {
            const userOrganization = userOrganizations?.find(
              (uo) => uo.userId === m.id
            );
            if (userOrganization) {
              return {
                id: userOrganization?.id,
                ...(m.organizationRole !== userOrganization?.role
                  ? { role: m.organizationRole }
                  : {}),
                ...((m.approverUserId ?? null) !==
                (userOrganization?.approverUserId ?? null)
                  ? { approverUserId: m.approverUserId ?? null }
                  : {}),
              };
            } else {
              // Use dummy id
              return { id: "" };
            }
          })
          .filter((uo) => Object.keys(uo).length > 1);

        // Remove
        // Remove users that have been removed from the team
        // If the user is an accountant, only remove if the selected organization is an accountant organization

        const toRemoveUserOrganizations:
          | UpdateUserOrganizationInput[]
          | undefined = userOrganizations
          ?.filter(
            (uo) =>
              !uo.deletedAt && !membersUpdated?.find((m) => m.id === uo.userId)
          )
          ?.map((uo) => {
            return {
              id: uo.id,
            };
          });

        const toRemoveUserBookkeepings:
          | UpdateUserBookkeepingInput[]
          | undefined = userBookkeepings
          ?.filter((ub) => {
            const member = membersUpdated?.find((m) => m.id === ub.userId);

            // If the user is no longer in the updated list, remove all bookkeepings
            if (!member) return true;

            // Find all bookkeeping assignments for the user in `membersUpdated`
            const updatedMemberBookkeepingIds = userBookkeepings
              ?.filter((ub) => ub.userId === member.id)
              .map((ub) => ub.bookkeepingId);

            // If the bookkeeping ID of `ub` is NOT in the updated member's bookkeepings, remove it
            return !updatedMemberBookkeepingIds.includes(ub.bookkeepingId);
          })
          ?.map((ub) => {
            return {
              id: ub.id,
            };
          });

        const toRemoveAccountingSystemBills:
          | UpdateAccountingSystemBillInput[]
          | undefined = toRemoveUserBookkeepings
          ?.map((rub) => {
            const accountingSystemBill = accountingSystemBills?.find(
              (asb) => asb.userBookkeepingId === rub.id
            );
            if (accountingSystemBill) {
              return {
                id: accountingSystemBill.id,
              };
            } else {
              return undefined;
            }
          })
          .filter(Boolean) as UpdateAccountingSystemBillInput[];

        // Execute the mutations in parallel
        const createUsersPromises = toAddUsers?.map((u) =>
          API.graphql<GraphQLQuery<CreateUserInput>>(
            graphqlOperation(createUser, {
              input: u,
            })
          )
        );

        const createUserOrganizationPromises = toAddUserOrganizations?.map(
          (uo) =>
            API.graphql<GraphQLQuery<CreateUserOrganizationInput>>(
              graphqlOperation(createUserOrganization, {
                input: uo,
              })
            )
        );

        const createUserBookkeepingPromises = toAddUserBookkeepings?.map((ub) =>
          API.graphql<GraphQLQuery<CreateUserBookkeepingInput>>(
            graphqlOperation(createUserBookkeeping, {
              input: ub,
            })
          )
        );

        const createAccountingSystemBillsPromises =
          toAddAccountingSystemBills.map((asb) =>
            API.graphql<GraphQLQuery<CreateAccountingSystemBillInput>>(
              graphqlOperation(createAccountingSystemBill, {
                input: asb,
              })
            )
          );

        const updateUsersCognitoPromises = toUpdateUsers?.map(async (u) => {
          API.post("AdminQueries", "/updateUser", {
            headers: headers,
            body: {
              userId: u.id,
              email: u.email,
              lastName: u.lastName,
              firstName: u.firstName,
              organizationId: selectedOrganization?.id,
            },
          });
        });

        const updateUserPromises = toUpdateUsers?.map((u) =>
          API.graphql<GraphQLQuery<UpdateUserInput>>(
            graphqlOperation(updateUser, {
              input: u,
            })
          )
        );

        const updateUserOrganizationPromises = toUpdateUserOrganizations?.map(
          (uo) =>
            API.graphql<GraphQLQuery<UpdateUserOrganizationInput>>(
              graphqlOperation(updateUserOrganization, {
                input: uo,
              })
            )
        );

        const removeUserPromises = upcomingInvoiceModalProps.toRemoveUsers?.map(
          (u) => deleteItem(updateUser, u.id)
        );

        const removeUserOrganizationPromises = toRemoveUserOrganizations?.map(
          (uo) => deleteItem(updateUserOrganization, uo.id)
        );

        const removeUserBookkeepingPromises = toRemoveUserBookkeepings?.map(
          (ub) => deleteItem(updateUserBookkeeping, ub.id)
        );

        const removeAccountingSystemBillsPromises =
          toRemoveAccountingSystemBills.map((asb) =>
            deleteItem(updateAccountingSystemBill, asb.id)
          );

        try {
          const results = await Promise.allSettled([
            ...(createUsersPromises ?? []),
            ...(createUserOrganizationPromises ?? []),
            ...(createUserBookkeepingPromises ?? []),
            ...(createAccountingSystemBillsPromises ?? []),
            ...(updateUsersCognitoPromises ?? []),
            ...(updateUserPromises ?? []),
            ...(updateUserOrganizationPromises ?? []),
            ...(removeUserPromises ?? []),
            ...(removeUserOrganizationPromises ?? []),
            ...(removeUserBookkeepingPromises ?? []),
            ...(removeAccountingSystemBillsPromises ?? []),
          ]);

          // Check for any rejections (errors) in the results
          const errors = results.filter((r) => r.status === "rejected");

          if (errors.length > 0) {
            // Handle errors - display error toast
            toast({
              title: "Fehler beim Speichern",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } else {
            // No errors, display success/info toast accordingly
            if (
              (toAddUsers && toAddUsers.length > 0) ||
              (toAddUserOrganizations && toAddUserOrganizations.length > 0) ||
              (toAddUserBookkeepings && toAddUserBookkeepings.length > 0) ||
              (toAddAccountingSystemBills &&
                toAddAccountingSystemBills.length > 0) ||
              (toUpdateUsers && toUpdateUsers.length > 0) ||
              (toUpdateUserOrganizations &&
                toUpdateUserOrganizations.length > 0) ||
              (upcomingInvoiceModalProps.toRemoveUsers &&
                upcomingInvoiceModalProps.toRemoveUsers.length > 0) ||
              (toRemoveUserOrganizations &&
                toRemoveUserOrganizations.length > 0) ||
              (toRemoveUserBookkeepings &&
                toRemoveUserBookkeepings.length > 0) ||
              (toRemoveAccountingSystemBills &&
                toRemoveAccountingSystemBills.length > 0)
            ) {
              // Update subscription if there are changes
              if (upcomingInvoiceModalProps.upcomingInvoice) {
                try {
                  await API.post(
                    "expenslyREST",
                    "/billing/update-subscription",
                    {
                      body: {
                        subscriptionId:
                          selectedOrganization.billing?.subscriptionId,
                        subscriptionItemId:
                          selectedOrganization.billing?.subscriptionItemId,
                        // Authoritative target seat count from the preview
                        // step (falls back to last invoice line for safety).
                        quantity:
                          upcomingInvoiceModalProps.targetQuantity ??
                          upcomingInvoiceModalProps.upcomingInvoice.lines.data[
                            upcomingInvoiceModalProps.upcomingInvoice.lines
                              .data.length - 1
                          ].quantity,
                        // On reduction omit prorationDate (backend →
                        // proration_behavior "none"); on addition reuse the
                        // preview's proration_date for preview/apply parity.
                        prorationDate: upcomingInvoiceModalProps.isReduction
                          ? undefined
                          : upcomingInvoiceModalProps.upcomingInvoice
                              ?.subscription_proration_date,
                      },
                    }
                  );
                } catch (error) {
                  toast({
                    title: "Team gespeichert, Abrechnung fehlgeschlagen",
                    description:
                      "Änderungen wurden übernommen, aber Stripe-Update schlug fehl. Support kontaktieren.",
                    status: "error",
                    duration: 7000,
                    isClosable: true,
                  });
                  return;
                }
              }

              toast({
                title: "Team gespeichert",
                status: "success",
              });
            } else {
              toast({
                title: "Keine Änderungen",
                status: "info",
              });
            }
          }
        } catch (error) {
          // Catch any other errors that may occur
          toast({
            title: "Fehler beim Speichern",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setUpcomingInvoiceModalProps({
            closed: true,
          });
        }
      };
      updateTeam();
    }
  }, [
    accountingSystemBills,
    accountingSystemConfigs,
    selectedOrganization,
    toast,
    upcomingInvoiceModalProps,
    upcomingInvoiceModalProps.closed,
    upcomingInvoiceModalProps.confirmed,
    upcomingInvoiceModalProps.membersFlat,
    upcomingInvoiceModalProps.toAddUsersCognito,
    upcomingInvoiceModalProps.toRemoveUsers,
    upcomingInvoiceModalProps.userFirst,
    userBookkeepings,
    userOrganizations,
    users,
  ]);

  return (
    <VStack width="full" height="full" alignItems="start" spacing="20px">
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={buttonRef}
        onClose={onModalClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent m={4}>
            <AlertDialogHeader
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pb={0}
            >
              <Text fontSize="lg" fontWeight="semibold">
                Abonnementänderung
              </Text>

              <IconButton
                variant="ghost"
                aria-label="Close"
                icon={
                  <Flex
                    boxSize="20px"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <IconCancel stroke={gray700} />
                  </Flex>
                }
                onClick={onModalClose}
              />
            </AlertDialogHeader>
            {upcomingInvoiceModalProps.upcomingInvoice && (
              <AlertDialogBody color="gray.600">
                {upcomingInvoiceModalProps.upcomingInvoice
                  .subscription_proration_date && (
                  <>
                    <Text fontSize={14} color="gray.600">
                      {`Die Abrechnung erfolgt pro rata per ${formatDate(
                        new Date(
                          upcomingInvoiceModalProps.upcomingInvoice
                            .subscription_proration_date * 1000
                        )
                      )}.`}
                    </Text>
                    <br />
                  </>
                )}
                <Text fontSize={14} fontWeight="semibold" color="gray.700">
                  {
                    upcomingInvoiceModalProps.upcomingInvoice.lines.data[0]
                      .description
                  }
                </Text>
                <Text fontSize={14} color="gray.600">
                  {`${(
                    upcomingInvoiceModalProps.upcomingInvoice.lines.data[0]
                      .amount / 100
                  ).toFixed(
                    2
                  )} ${upcomingInvoiceModalProps.upcomingInvoice.lines.data[0].currency.toUpperCase()} für ${
                    upcomingInvoiceModalProps.upcomingInvoice.lines.data[0]
                      .quantity
                  } Benutzer`}
                </Text>
                {upcomingInvoiceModalProps.upcomingInvoice.lines.data.length >
                  1 && (
                  <Flex direction="column">
                    <br />
                    <Text fontSize={14} fontWeight="semibold" color="gray.700">
                      {
                        upcomingInvoiceModalProps.upcomingInvoice.lines.data[1]
                          .description
                      }
                    </Text>
                    <Text fontSize={14} color="gray.600">
                      {`${(
                        upcomingInvoiceModalProps.upcomingInvoice.lines.data[1]
                          .amount / 100
                      ).toFixed(
                        2
                      )} ${upcomingInvoiceModalProps.upcomingInvoice.lines.data[1].currency.toUpperCase()} für ${
                        upcomingInvoiceModalProps.upcomingInvoice.lines.data[1]
                          .quantity
                      } Benutzer`}
                    </Text>
                  </Flex>
                )}
                <br />
                <Text fontSize={14} fontWeight="semibold" color="gray.700">
                  Abrechnung
                </Text>
                <Text fontSize={14} color="gray.600">
                  {`Zwischensumme ${(
                    upcomingInvoiceModalProps.upcomingInvoice.subtotal / 100
                  ).toFixed(
                    2
                  )} ${upcomingInvoiceModalProps.upcomingInvoice.currency.toUpperCase()}`}
                </Text>
                <Text fontSize={14} color="gray.600">
                  {`MwSt. ${(
                    upcomingInvoiceModalProps.upcomingInvoice.tax / 100
                  ).toFixed(
                    2
                  )} ${upcomingInvoiceModalProps.upcomingInvoice.currency.toUpperCase()}`}
                </Text>
                <Text fontSize={14} fontWeight="semibold" color="gray.700">
                  {`Gesamt ${(
                    upcomingInvoiceModalProps.upcomingInvoice.total / 100
                  ).toFixed(
                    2
                  )} ${upcomingInvoiceModalProps.upcomingInvoice.currency.toUpperCase()}`}
                </Text>
                {upcomingInvoiceModalProps.upcomingInvoice.total < 0 && (
                  <Flex direction="column">
                    <br />
                    <Text fontSize={14} color="gray.600">
                      Der Betrag wird in der nächsten Abrechnungsperiode
                      abgezogen.
                    </Text>
                  </Flex>
                )}
              </AlertDialogBody>
            )}
            <AlertDialogFooter flexDirection="column">
              <Button
                ref={buttonRef}
                colorScheme="primary"
                onClick={() => {
                  setUpcomingInvoiceModalProps({
                    ...upcomingInvoiceModalProps,
                    confirmed: true,
                  });
                }}
                isLoading={upcomingInvoiceModalProps.confirmed}
                width="full"
              >
                Bestätigen
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <HStack width="full" justifyContent="space-between" alignItems="start">
        <VStack alignItems="start" spacing="4px">
          <Text fontSize={18} fontWeight="semibold" color="gray.900">
            {typeNamePlural}
          </Text>
          <Text fontSize={14} color="gray.600">
            {`Hier kannst du deine ${typeNamePlural} verwalten`}
          </Text>
        </VStack>
        <ShowDeletedToggle
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          hasDeletedItems={hasDeletedItems}
        />
      </HStack>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={TeamSchema}
        validateOnChange={false}
        onSubmit={async (values) => {
          if (selectedOrganization) {
            // get first real user to get authGroups
            const userFirst = users?.find(
              (u) =>
                selectedOrganization.type === OrganizationType.accountant ||
                u.role !== UserAppRole.accountant
            )!;

            const membersFlat: TeamValueFlat[] | undefined = values.members
              ?.filter((m) => !m.deletedAt)
              .flatMap((m) =>
                m.bookkeepings.length > 0
                  ? m.bookkeepings.map((b) => ({
                      ...m,
                      bookkeepingId: b.id,
                    }))
                  : m
              );

            // Prepare the mutations needed to calculate the new invoice seats count
            const toAddUsersCognito = getToAddUsers(
              selectedOrganization,
              userFirst,
              membersFlat,
              users
            );

            const toRemoveUsers: UpdateUserInput[] | undefined = users
              ?.filter(
                (u) =>
                  !u.deletedAt &&
                  !membersFlat
                    ?.filter((m) => !m.deletedAt)
                    .find((m) => m.id === u.id) &&
                  (u.role !== UserAppRole.accountant ||
                    selectedOrganization.type === OrganizationType.accountant)
              )
              ?.map((u) => {
                return {
                  id: u.id,
                };
              });

            // Update seats count in billing
            let seatsCount =
              (userOrganizations?.filter(
                (uo) =>
                  !uo.deletedAt && uo.role !== UserOrganizationRole.accountant
              ).length ?? 0) +
              (toAddUsersCognito?.length ?? 0) -
              (toRemoveUsers?.length ?? 0);
            seatsCount =
              selectedOrganization.logo === aerzteTreuhandLogo
                ? // ÄT has a minimum of 3 seats
                  Math.max(seatsCount, 3)
                : seatsCount;
            const billingQuantity =
              selectedOrganization?.billing?.quantity ?? 0;

            if (
              // Make sure to only apply if seatsCount could be calculated
              seatsCount > 0 &&
              // Make sure to only apply if billingQuantity is set
              billingQuantity > 0 &&
              // Make sure to only apply if there are changes in seatsCount
              seatsCount !== billingQuantity
            ) {
              // Asymmetric proration: charge prorated for additions, defer
              // credit on removals to next renewal (omit prorationDate so
              // backend uses proration_behavior: "none"). Calendar
              // endOfMonth doesn't match Stripe period anchor for orgs not
              // signed up on the 1st — would cause Stripe 400.
              const isReduction = seatsCount < billingQuantity;
              const prorationDate = isReduction
                ? undefined
                : getUnixTime(new Date());
              try {
                const response = await API.get(
                  "expenslyREST",
                  "/billing/retrieve-upcoming-invoice",
                  {
                    queryStringParameters: {
                      subscriptionId:
                        selectedOrganization.billing?.subscriptionId,
                      subscriptionItemId:
                        selectedOrganization.billing?.subscriptionItemId,
                      quantity: seatsCount,
                      ...(prorationDate !== undefined ? { prorationDate } : {}),
                    },
                  }
                );

                if (response) {
                  const upcomingInvoice = response as UpcomingInvoice;
                  setUpcomingInvoiceModalProps({
                    upcomingInvoice,
                    targetQuantity: seatsCount,
                    isReduction,
                    closed: false,
                    userFirst,
                    membersFlat,
                    toAddUsersCognito,
                    toRemoveUsers,
                  });
                }
              } catch (error) {
                toast({
                  title: "Vorschau der Rechnung fehlgeschlagen",
                  description:
                    "Bitte später erneut versuchen oder Support kontaktieren.",
                  status: "error",
                  duration: 5000,
                  isClosable: true,
                });
              }
            } else {
              setUpcomingInvoiceModalProps({
                closed: true,
                userFirst,
                membersFlat,
                toAddUsersCognito,
                toRemoveUsers,
              });
            }
          }
        }}
      >
        {({ values, isSubmitting }) => (
          <Form
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            <VStack width="full" height="full" justify="space-between">
              <FieldArray name="members">
                {({ remove, replace, push }) => (
                  <TeamTable
                    typeNamePlural={typeNamePlural}
                    selectedOrganization={selectedOrganization}
                    bookkeepings={bookkeepings}
                    users={users}
                    team={values}
                    remove={remove}
                    replace={replace}
                    push={push}
                  />
                )}
              </FieldArray>
              <Divider />
              <HStack width="full" justify="end">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate("/");
                  }}
                  isDisabled={isSubmitting}
                >
                  Abbrechen
                </Button>
                <Button
                  colorScheme="primary"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Speichern
                </Button>
              </HStack>
            </VStack>
          </Form>
        )}
      </Formik>
    </VStack>
  );
};

export default Team;

const getToAddUsers = (
  selectedOrganization: OrganizationFull | undefined,
  userFirst: User,
  members?: TeamValueFlat[],
  users?: User[]
) => {
  const filteredAndMapped = members
    ?.filter(
      (m) =>
        (selectedOrganization?.type === OrganizationType.accountant ||
          m.appRole !== UserAppRole.accountant) &&
        !users?.find((u) => u.id === m.id)
    )
    .map((m) => {
      return {
        id: m.id,
        lastName: m.lastName,
        firstName: m.firstName,
        email: m.email,
        role: m.appRole,
        authGroupsRead: userFirst.authGroupsRead,
        authGroupsUpdate: userFirst.authGroupsUpdate,
      };
    });

  // Remove duplicates
  if (!filteredAndMapped) return undefined;

  return Array.from(
    new Map(filteredAndMapped.map((user) => [user.id, user])).values()
  );
};
